import { useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { textInputRows } from '../../utils/webStyles';
import { DEFAULT_EVENT_TIME_ZONE } from '../../schemas/firestoreSchemas';
import { utcToZonedLocal, zonedLocalToUtc } from '../../utils/eventCalendar';
import type { PlanEventInput } from '../../api/planEvents';
import type { PlanEvent } from '../../types';

const TITLE_MAX = 80;
const PLACE_MAX = 120;
const BRING_MAX = 300;
const NOTES_MAX = 600;

/**
 * A browser date or time picker. React Native has no such control and the
 * plan editor is web only, so this renders the native <input>, styled with
 * the same tokens as `Input compact`. Text fields use `Input`; this exists
 * only because RN's TextInput cannot be type="date".
 */
function PickerField({
  label,
  type,
  value,
  onChange,
  testID,
  error,
}: {
  label: string;
  type: 'date' | 'time';
  value: string;
  onChange: (next: string) => void;
  testID: string;
  error?: boolean;
}) {
  const { colors, isDark } = useTheme();
  return (
    <View style={styles.pickerField}>
      <Text style={[styles.pickerLabel, { color: colors.textSecondary }]}>{label}</Text>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testID}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        style={{
          boxSizing: 'border-box',
          width: '100%',
          fontFamily: TYPOGRAPHY.bodySmall.fontFamily,
          fontSize: TYPOGRAPHY.bodySmall.fontSize,
          color: colors.text,
          backgroundColor: colors.background,
          border: `1px solid ${error ? colors.danger : colors.border}`,
          borderRadius: BORDER_RADIUS.sm,
          padding: `${SPACING.sm}px ${SPACING.smd}px`,
          // Dark theme: otherwise the picker's own icon is black on ink.
          colorScheme: isDark ? 'dark' : 'light',
        }}
      />
    </View>
  );
}

interface FormValues {
  title: string;
  date: string;
  start: string;
  end: string;
  place: string;
  bring: string;
  notes: string;
}

function initialValues(event: PlanEvent | undefined, bundleName: string): FormValues {
  if (event) {
    const tz = event.timeZone;
    const start = utcToZonedLocal(event.startsAt.toDate(), tz);
    const end = utcToZonedLocal(event.endsAt.toDate(), tz);
    return {
      title: event.title,
      date: start.date,
      start: start.time,
      end: end.time,
      place: event.meetingPlace.label,
      bring: event.whatToBring ?? '',
      notes: event.notes ?? '',
    };
  }
  return {
    title: bundleName.slice(0, TITLE_MAX),
    date: '',
    start: '09:00',
    end: '12:00',
    place: '',
    bring: '',
    notes: '',
  };
}

export interface PlanEventFormProps {
  /** The bundle's name, which prefills the title of a new event. */
  bundleName: string;
  /** Where a new event's pin starts: the middle of the bundle's defenses. */
  defaultPlace: { lat: number; lng: number } | null;
  /** Present when editing; absent when scheduling a new event. */
  event?: PlanEvent;
  saving: boolean;
  /** Resolves true when the save went through, so the form can close. */
  onSubmit: (input: PlanEventInput) => Promise<boolean>;
  onCancel: () => void;
  /** An error from the last save, shown above the buttons. */
  serverError?: string | null;
}

/**
 * Create and edit form for a bundle event. Times are typed as local time in
 * the event's zone (Los Angeles for a new event, the stored zone when
 * editing) and turned into instants by zonedLocalToUtc.
 */
export function PlanEventForm({
  bundleName,
  defaultPlace,
  event,
  saving,
  onSubmit,
  onCancel,
  serverError,
}: PlanEventFormProps) {
  const { colors } = useTheme();
  const timeZone = event?.timeZone ?? DEFAULT_EVENT_TIME_ZONE;
  const [values, setValues] = useState<FormValues>(() => initialValues(event, bundleName));
  const [submitted, setSubmitted] = useState(false);
  const set = <K extends keyof FormValues>(key: K, next: FormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: next }));

  const problems = useMemo(() => {
    const startsAt = zonedLocalToUtc(values.date, values.start, timeZone);
    const endsAt = zonedLocalToUtc(values.date, values.end, timeZone);
    return {
      title: values.title.trim() ? null : 'Add a title.',
      date: zonedLocalToUtc(values.date, '00:00', timeZone) ? null : 'Pick a date.',
      start: startsAt ? null : 'Pick a start time.',
      end: !values.end
        ? 'Pick an end time.'
        : startsAt && endsAt && endsAt.getTime() <= startsAt.getTime()
          ? 'The end has to be after the start.'
          : null,
      place: values.place.trim() ? null : 'Say where to meet.',
      startsAt,
      endsAt,
    };
  }, [values.date, values.start, values.end, values.title, values.place, timeZone]);

  const firstProblem =
    problems.title ?? problems.place ?? problems.date ?? problems.start ?? problems.end;

  const handleSave = async () => {
    setSubmitted(true);
    if (firstProblem || !problems.startsAt || !problems.endsAt) return;
    // An edit keeps the event's own pin; a new event starts at the bundle's middle.
    const lat = event?.meetingPlace.lat ?? defaultPlace?.lat;
    const lng = event?.meetingPlace.lng ?? defaultPlace?.lng;
    const input: PlanEventInput = {
      title: values.title.trim(),
      startsAt: problems.startsAt,
      endsAt: problems.endsAt,
      timeZone,
      meetingPlace: {
        label: values.place.trim(),
        ...(typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : {}),
      },
      whatToBring: values.bring.trim() || undefined,
      notes: values.notes.trim() || undefined,
    };
    await onSubmit(input);
  };

  const show = (message: string | null) => (submitted ? (message ?? undefined) : undefined);

  return (
    <View style={styles.form} testID="plan-event-form">
      <Input
        compact
        label="Title"
        value={values.title}
        onChangeText={(text) => set('title', text.slice(0, TITLE_MAX))}
        maxLength={TITLE_MAX}
        error={show(problems.title)}
        testID="plan-event-title"
      />
      <PickerField
        label="Date"
        type="date"
        value={values.date}
        onChange={(next) => set('date', next)}
        testID="plan-event-date"
        error={submitted && !!problems.date}
      />
      {submitted && problems.date && (
        <Text style={[styles.error, { color: colors.danger }]}>{problems.date}</Text>
      )}
      <View style={styles.timeRow}>
        <PickerField
          label="Start"
          type="time"
          value={values.start}
          onChange={(next) => set('start', next)}
          testID="plan-event-start"
          error={submitted && !!problems.start}
        />
        <PickerField
          label="End"
          type="time"
          value={values.end}
          onChange={(next) => set('end', next)}
          testID="plan-event-end"
          error={submitted && !!problems.end}
        />
      </View>
      {submitted && (problems.start || problems.end) && (
        <Text style={[styles.error, { color: colors.danger }]}>
          {problems.start ?? problems.end}
        </Text>
      )}
      <Input
        compact
        label="Meeting place"
        value={values.place}
        onChangeText={(text) => set('place', text.slice(0, PLACE_MAX))}
        placeholder="Fawnskin fire station"
        maxLength={PLACE_MAX}
        error={show(problems.place)}
        containerStyle={styles.afterPickers}
        testID="plan-event-place"
      />
      <Input
        compact
        label="What to bring (optional)"
        value={values.bring}
        onChangeText={(text) => set('bring', text.slice(0, BRING_MAX))}
        placeholder="Gloves, loppers, water"
        multiline
        maxLength={BRING_MAX}
        {...textInputRows(2)}
        testID="plan-event-bring"
      />
      <Input
        compact
        label="Notes (optional)"
        value={values.notes}
        onChangeText={(text) => set('notes', text.slice(0, NOTES_MAX))}
        placeholder="Parking, lunch, who to call"
        multiline
        maxLength={NOTES_MAX}
        {...textInputRows(3)}
        testID="plan-event-notes"
      />
      {serverError ? (
        <Text style={[styles.error, { color: colors.danger }]} testID="plan-event-error">
          {serverError}
        </Text>
      ) : null}
      <View style={styles.footer}>
        <Button
          title="Cancel"
          onPress={onCancel}
          variant="secondary"
          size="sm"
          style={styles.half}
          testID="plan-event-form-cancel"
        />
        <Button
          title={event ? 'Save changes' : 'Schedule event'}
          onPress={() => void handleSave()}
          variant="primary"
          size="sm"
          loading={saving}
          style={styles.half}
          testID="plan-event-save"
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: SPACING.xxs,
  },
  pickerField: {
    flex: 1,
    minWidth: 0,
    marginBottom: SPACING.smd,
  },
  pickerLabel: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
    marginBottom: SPACING.xs,
  },
  timeRow: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  afterPickers: {
    marginTop: SPACING.xxs,
  },
  error: {
    ...TYPOGRAPHY.caption,
    marginBottom: SPACING.xs,
  },
  footer: {
    flexDirection: 'row',
    gap: SPACING.sm,
    marginTop: SPACING.sm,
  },
  half: {
    flex: 1,
  },
});
